import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Paywall from "@/components/Paywall";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Utensils, ClipboardList, Sprout, CalendarDays, Clock, Printer, Crown, ShoppingCart, Check, FlaskConical } from "lucide-react";

function Recommendations({ items }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {items.map((r) => (
        <div key={r.nutrient} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6" data-testid={`reco-${r.nutrient}`}>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <h3 className="font-display text-lg font-bold text-[#1E3F20]">{r.nutrient}</h3>
            <span className="text-sm font-semibold text-[#C87D55]">{r.target}</span>
          </div>
          <p className="text-sm text-[#4A5D52] mb-2"><span className="font-medium text-[#1C2D24]">Quellen:</span> {r.sources}</p>
          <p className="text-sm text-[#769777] italic">{r.note}</p>
        </div>
      ))}
    </div>
  );
}

function Recipes({ items, intro }) {
  const cats = ["Alle", ...Array.from(new Set(items.map((r) => r.category)))];
  const [cat, setCat] = useState("Alle");
  const filtered = cat === "Alle" ? items : items.filter((r) => r.category === cat);
  return (
    <div>
      {intro && (
        <div data-testid="recipes-intro" className="mb-6 flex items-start gap-3 bg-[#E2EBE1] rounded-2xl p-5">
          <FlaskConical className="w-5 h-5 text-[#2D5A27] shrink-0 mt-0.5" />
          <p className="text-sm text-[#1E3F20] leading-relaxed">{intro}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="recipe-filter-category">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              cat === c ? "bg-[#1E3F20] text-white" : "bg-white text-[#4A5D52] ring-1 ring-[#E2DDD3] hover:bg-[#EBE7DF]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((r) => (
          <div key={r.id} data-testid={`recipe-card-${r.id}`} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6 overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
            {r.image && (
              <img
                src={r.image}
                alt={r.title}
                data-testid={`recipe-image-${r.id}`}
                loading="lazy"
                className="-mx-6 -mt-6 mb-4 h-44 object-cover"
                style={{ width: "calc(100% + 3rem)" }}
              />
            )}
            <div className="flex items-center gap-2 text-xs text-[#769777] mb-2">
              <Badge variant="secondary" className="bg-[#E2EBE1] text-[#2D5A27] hover:bg-[#E2EBE1]">{r.category}</Badge>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {r.time}</span>
            </div>
            <h3 className="font-display text-lg font-bold text-[#1C2D24] leading-snug">{r.title}</h3>
            <p className="text-sm text-[#C87D55] font-medium mt-1">Calcium: {r.calcium}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {r.tags.map((t) => (
                <span key={t} className="text-xs bg-[#F3EFE6] text-[#4A5D52] rounded-full px-2.5 py-0.5">{t}</span>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#E2DDD3] text-sm text-[#4A5D52] space-y-3 flex-1">
              <div>
                <p className="font-semibold text-[#1C2D24] mb-1">Zutaten</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {r.ingredients.map((ing) => <li key={ing}>{ing}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-[#1C2D24] mb-1">Zubereitung</p>
                <ol className="list-decimal list-inside space-y-0.5">
                  {r.steps.map((s) => <li key={s}>{s}</li>)}
                </ol>
              </div>
              {r.evidence && (
                <div className="mt-2 rounded-xl bg-[#F3EFE6] p-3">
                  <p className="text-xs font-semibold text-[#2D5A27] uppercase tracking-wide mb-1">Evidenz aus der Meta-Analyse</p>
                  <p className="text-xs text-[#4A5D52] leading-relaxed">{r.evidence}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Guide({ steps }) {
  return (
    <div className="space-y-4 max-w-3xl">
      {steps.map((s) => (
        <div key={s.step} className="flex gap-4 bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6">
          <div className="shrink-0 w-10 h-10 rounded-full bg-[#1E3F20] text-white font-display font-bold flex items-center justify-center">{s.step}</div>
          <div>
            <h3 className="font-semibold text-[#1C2D24]">{s.title}</h3>
            <p className="text-sm text-[#4A5D52] leading-relaxed mt-1">{s.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function DayPlan({ plan, totals }) {  return (
    <div className="max-w-3xl">
      <div className="flex justify-end mb-4 no-print">
        <Button data-testid="recipe-print-button" onClick={() => window.print()} variant="outline" className="rounded-full border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white">
          <Printer className="w-4 h-4 mr-2" /> Drucken
        </Button>
      </div>
      <div className="space-y-3">
        {plan.map((m) => (
          <div key={m.meal} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-5">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="w-4 h-4 text-[#2D5A27]" />
              <h3 className="font-semibold text-[#1E3F20]">{m.meal}</h3>
            </div>
            <p className="text-sm text-[#1C2D24]">{m.text}</p>
            <p className="text-xs text-[#769777] mt-1">{m.nutrients}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 bg-[#E2EBE1] rounded-2xl p-5 text-sm text-[#1E3F20] font-medium">{totals}</div>
    </div>
  );
}

function MarketAnalysis({ data }) {
  return (
    <div data-testid="market-analysis">
      <p className="text-[#4A5D52] leading-relaxed max-w-3xl">{data.intro}</p>
      <p className="text-xs text-[#769777] mt-2">{data.region}</p>

      <div className="mt-6 overflow-x-auto rounded-2xl ring-1 ring-[#E2DDD3]">
        <table className="w-full text-left text-sm min-w-[720px] bg-white">
          <thead className="bg-[#1E3F20] text-[#E2EBE1]">
            <tr>
              <th className="px-4 py-3 font-semibold">Präparat</th>
              <th className="px-4 py-3 font-semibold">Marke</th>
              <th className="px-4 py-3 font-semibold">Stämme</th>
              <th className="px-4 py-3 font-semibold">KBE/Tag</th>
              <th className="px-4 py-3 font-semibold">Packung</th>
              <th className="px-4 py-3 font-semibold">Preis</th>
              <th className="px-4 py-3 font-semibold">€/Tag</th>
            </tr>
          </thead>
          <tbody>
            {data.products.map((p, i) => (
              <tr key={p.name} className={i % 2 ? "bg-[#F9F8F3]" : ""}>
                <td className="px-4 py-3 font-medium text-[#1C2D24]">{p.name}</td>
                <td className="px-4 py-3 text-[#4A5D52]">{p.brand}</td>
                <td className="px-4 py-3 text-[#4A5D52]">{p.strains}</td>
                <td className="px-4 py-3 text-[#C87D55] whitespace-nowrap">{p.kbe}</td>
                <td className="px-4 py-3 text-[#4A5D52] whitespace-nowrap">{p.pack}</td>
                <td className="px-4 py-3 font-semibold text-[#1E3F20] whitespace-nowrap">{p.price}</td>
                <td className="px-4 py-3 text-[#4A5D52] whitespace-nowrap">{p.per_day}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.products.map((p) => (
          <div key={p.name} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-5">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="font-display font-bold text-[#1E3F20]">{p.name}</h3>
              <span className="text-sm font-semibold text-[#C87D55] whitespace-nowrap">{p.price}</span>
            </div>
            <p className="text-xs text-[#769777]">{p.brand} · {p.strains} Stämme · {p.kbe} KBE</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {p.badges.map((b) => (
                <span key={b} className="text-xs bg-[#E2EBE1] text-[#2D5A27] rounded-full px-2.5 py-0.5">{b}</span>
              ))}
            </div>
            <p className="text-sm text-[#4A5D52] mt-3 leading-relaxed">{p.note}</p>
            <p className="text-xs text-[#769777] mt-2">Bezug: {p.source}</p>
          </div>
        ))}
      </div>

      <h3 className="font-display text-xl font-bold text-[#1C2D24] mt-10 mb-4">Empfehlung nach Szenario</h3>
      <div className="grid md:grid-cols-2 gap-4">
        {data.recommendations.map((r) => (
          <div key={r.scenario} className="bg-[#E2EBE1] rounded-2xl p-5">
            <p className="text-xs uppercase tracking-widest text-[#2D5A27] font-semibold">{r.scenario}</p>
            <p className="font-display text-lg font-bold text-[#1E3F20] mt-1">{r.product}</p>
            <p className="text-sm text-[#4A5D52] mt-1 leading-relaxed">{r.why}</p>
          </div>
        ))}
      </div>

      <h3 className="font-display text-xl font-bold text-[#1C2D24] mt-10 mb-4">Hinweise zur Auswahl</h3>
      <ul className="space-y-2 max-w-3xl">
        {data.tips.map((t, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#4A5D52]"><Check className="w-5 h-5 text-[#2D5A27] shrink-0 mt-0.5" />{t}</li>
        ))}
      </ul>

      <p className="mt-8 text-xs text-[#769777] bg-[#FCF3E7] border border-[#E8D3B5] rounded-xl p-4">{data.disclaimer}</p>
    </div>
  );
}

export default function Premium() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.is_paid) {
      api.get("/premium/content").then(({ data }) => setData(data)).catch(() => {});
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-[#F9F8F3]">
      <Navbar />
      {!user?.is_paid ? (
        <Paywall />
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#E2EBE1] flex items-center justify-center">
              <Crown className="w-6 h-6 text-[#C87D55]" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1C2D24]">Dein Premium-Bereich</h1>
              <p className="text-[#4A5D52] text-sm">Willkommen, {user.name || "FoodBone-Mitglied"} – hier findest du alle konkreten Inhalte.</p>
            </div>
          </div>

          {!data ? (
            <div className="h-64 rounded-3xl bg-[#F3EFE6] animate-pulse" />
          ) : (
            <Tabs defaultValue="recipes" className="w-full">
              <TabsList className="bg-[#F3EFE6] rounded-full p-1 flex flex-wrap h-auto gap-1 mb-8">
                <TabsTrigger value="recipes" data-testid="premium-recipes-tab" className="rounded-full data-[state=active]:bg-[#1E3F20] data-[state=active]:text-white px-5 py-2">
                  <Utensils className="w-4 h-4 mr-2" /> Rezepte
                </TabsTrigger>
                <TabsTrigger value="reco" data-testid="premium-recommendations-tab" className="rounded-full data-[state=active]:bg-[#1E3F20] data-[state=active]:text-white px-5 py-2">
                  <ClipboardList className="w-4 h-4 mr-2" /> Empfehlungen
                </TabsTrigger>
                <TabsTrigger value="guide" data-testid="premium-guide-tab" className="rounded-full data-[state=active]:bg-[#1E3F20] data-[state=active]:text-white px-5 py-2">
                  <Sprout className="w-4 h-4 mr-2" /> Probiotika-Kur
                </TabsTrigger>
                <TabsTrigger value="plan" data-testid="premium-plan-tab" className="rounded-full data-[state=active]:bg-[#1E3F20] data-[state=active]:text-white px-5 py-2">
                  <CalendarDays className="w-4 h-4 mr-2" /> Tagesplan
                </TabsTrigger>
                <TabsTrigger value="market" data-testid="premium-market-tab" className="rounded-full data-[state=active]:bg-[#1E3F20] data-[state=active]:text-white px-5 py-2">
                  <ShoppingCart className="w-4 h-4 mr-2" /> Marktanalyse
                </TabsTrigger>
              </TabsList>
              <TabsContent value="recipes"><Recipes items={data.recipes} intro={data.recipes_intro} /></TabsContent>
              <TabsContent value="reco"><Recommendations items={data.recommendations} /></TabsContent>
              <TabsContent value="guide"><Guide steps={data.probiotic_guide} /></TabsContent>
              <TabsContent value="plan"><DayPlan plan={data.day_plan} totals={data.day_plan_totals} /></TabsContent>
              <TabsContent value="market"><MarketAnalysis data={data.market_analysis} /></TabsContent>
            </Tabs>
          )}
        </main>
      )}
      <Footer />
    </div>
  );
}
